import { cn } from '@lms/ui/utils';
import { useMemo, useState } from 'react';
import { Cell, Pie, PieChart, Sector, Tooltip } from 'recharts';
import { formatNumber, formatPercent } from '../format';
import { OTHER_KEY, type SeriesSet, sumBy } from '../series';
import { ChartEmpty, FloatingTooltip, Swatch } from './parts';

export interface DonutChartProps<R> {
  data: readonly R[];
  name: (row: R) => string;
  value: (row: R) => number;
  /** Keyed on `name`; build it with a low `max` (≈6) for a readable donut. */
  seriesSet: SeriesSet;
  /** Caption under the total in the hole, e.g. "trainings". */
  centerLabel?: string;
  locale?: string;
  size?: number;
  emptyLabel?: string;
  /**
   * Makes slices and legend rows clickable (not the folded "Other" slice),
   * e.g. to filter the page by the clicked series. Gets the series name.
   */
  onSelect?: (name: string) => void;
}

interface Slice {
  key: string;
  name: string;
  color: string;
  value: number;
}

/**
 * Part-to-whole donut with the total in the hole and a legend that carries
 * each slice's value and share, so nothing depends on hovering.
 */
export function DonutChart<R>({
  data,
  name,
  value,
  seriesSet,
  centerLabel,
  locale,
  size = 168,
  emptyLabel = 'No data for this period',
  onSelect,
}: DonutChartProps<R>) {
  const [active, setActive] = useState<number | undefined>();

  const slices = useMemo<Slice[]>(() => {
    const totals = sumBy(data, (r) => seriesSet.keyOf(name(r)), value);
    return seriesSet.series
      .map((s) => ({ ...s, value: totals.get(s.key) ?? 0 }))
      .filter((s) => s.value > 0);
  }, [data, name, value, seriesSet]);

  const total = slices.reduce((sum, s) => sum + s.value, 0);
  if (total === 0) return <ChartEmpty label={emptyLabel} height={size} />;

  const outer = size / 2 - 6;
  const selectable = (s: Slice) =>
    onSelect !== undefined && s.key !== OTHER_KEY;
  return (
    <div className="flex flex-col items-center gap-5">
      <div className="relative shrink-0" style={{ width: size, height: size }}>
        <PieChart width={size} height={size}>
          <Tooltip
            content={({ active: on, payload, coordinate }) => {
              const slice = payload?.[0]?.payload as Slice | undefined;
              if (!on || !slice) return null;
              return (
                <FloatingTooltip coordinate={coordinate}>
                  <div className="bg-background border-border/60 flex max-w-72 items-center gap-2 rounded-lg border px-3 py-2 text-xs shadow-lg">
                    <Swatch color={slice.color} />
                    <span className="text-muted-foreground min-w-0 break-words">
                      {slice.name}
                    </span>
                    <span className="text-foreground font-medium tabular-nums">
                      {formatNumber(slice.value, locale)}
                    </span>
                  </div>
                </FloatingTooltip>
              );
            }}
          />
          <Pie
            data={slices}
            dataKey="value"
            nameKey="name"
            innerRadius={outer * 0.68}
            outerRadius={outer}
            startAngle={90}
            endAngle={-270}
            stroke="var(--card)"
            strokeWidth={2}
            isAnimationActive={false}
            activeIndex={active}
            activeShape={(props: object) => (
              <Sector
                {...props}
                outerRadius={outer + 4}
                stroke="var(--card)"
                strokeWidth={2}
              />
            )}
            onMouseEnter={(_, i) => setActive(i)}
            onMouseLeave={() => setActive(undefined)}
            onClick={(_, i) => {
              const s = slices[i];
              if (s && selectable(s)) onSelect?.(s.name);
            }}
          >
            {slices.map((s) => (
              <Cell
                key={s.key}
                fill={s.color}
                className={selectable(s) ? 'cursor-pointer' : undefined}
              />
            ))}
          </Pie>
        </PieChart>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl leading-none font-semibold tracking-tight">
            {formatNumber(total, locale)}
          </span>
          {centerLabel && (
            <span className="text-muted-foreground mt-1 text-xs">
              {centerLabel}
            </span>
          )}
        </div>
      </div>

      <ul className="flex w-full max-w-sm min-w-0 flex-col gap-1.5 text-sm">
        {slices.map((s, i) => {
          const content = (
            <>
              <Swatch color={s.color} />
              <span className="min-w-0 flex-1 truncate" title={s.name}>
                {s.name}
              </span>
              <span className="font-medium tabular-nums">
                {formatNumber(s.value, locale)}
              </span>
              <span className="text-muted-foreground w-10 text-right text-xs tabular-nums">
                {formatPercent(s.value / total, locale)}
              </span>
            </>
          );
          const row = 'flex items-center gap-2 rounded px-1 transition-colors';
          return (
            <li
              key={s.key}
              onMouseEnter={() => setActive(i)}
              onMouseLeave={() => setActive(undefined)}
            >
              {selectable(s) ? (
                <button
                  type="button"
                  onClick={() => onSelect?.(s.name)}
                  className={cn(
                    row,
                    'focus-visible:ring-ring w-full text-left focus-visible:ring-2 focus-visible:outline-none',
                    active === i && 'bg-muted',
                  )}
                >
                  {content}
                </button>
              ) : (
                <div className={cn(row, active === i && 'bg-muted')}>
                  {content}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
